import { watch } from 'vue'

import { useDataExportStore } from '../stores/dataExportStore'

import { useWorkspace } from '@/features/account'
import { registerSessionReset } from '@/features/auth'

/**
 * Install module 16's scope resets once from the app composition root (contract §7).
 *
 * Both edges clear the WHOLE store, draft included — the draft is the load-bearing half: it holds
 * workspace A's selected account ids, and submitting those under workspace B is exactly the
 * cross-tenant write a persisted draft would have allowed. Jobs, limits and templates are
 * `{accountUserId, workspaceId}`-scoped for the same reason.
 *
 * The feature owns no bootstrap section, so the workspace edge is a `watch` on 07's `workspaceId`
 * (the `registerOrpheusLifecycle` / `registerAlertsLifecycle` idiom) rather than a hydrator reset.
 */
export function registerDataExportLifecycle(): () => void {
  const store = useDataExportStore()
  const { workspaceId } = useWorkspace()
  const stopWorkspace = watch(
    workspaceId,
    (next, previous) => {
      if (String(next ?? '') !== String(previous ?? '')) store.reset()
    },
    { flush: 'sync' },
  )
  const unregisterSession = registerSessionReset(() => store.reset())
  return () => {
    stopWorkspace()
    unregisterSession()
  }
}
